/**
 * Section Component
 *
 * A layout primitive for page sections with background variants
 * and vertical rhythm. Uses semantic tokens from the design system.
 *
 * Features:
 * - Background color variants using design tokens
 * - Vertical rhythm (spacing) variants for page flow
 * - Semantic section element with proper landmarks
 * - No arbitrary values - all colors from design tokens
 *
 * @example Basic section
 * ```tsx
 * <Section>
 * <Container>
 * <Heading>Section Title</Heading>
 * <Content />
 * </Container>
 * </Section>
 * ```
 *
 * @example With background variants
 * ```tsx
 * <Section background="muted">Subtle background</Section>
 * <Section background="primary">Primary brand background</Section>
 * <Section background="surface">Elevated surface</Section>
 * ```
 *
 * @example With vertical rhythm
 * ```tsx
 * <Section spacing="lg">Large vertical padding</Section>
 * <Section spacing="xl">Extra large for hero sections</Section>
 * ```
 */

import * as React from'react';
import type {VariantProps} from'class-variance-authority';
import {cn} from'@/shared/utils/utils';
import {sectionVariants} from'./Section.variants';

// =============================================================================
// TYPES
// =============================================================================

interface SectionProps
 extends React.HTMLAttributes<HTMLElement>,
 VariantProps<typeof sectionVariants> {
 /**
 * Accessible label for the section (for screen readers)
 */
"aria-label"?: string;
 /**
 * ID of an element that labels this section
 */
"aria-labelledby"?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Section - Page section layout primitive
 *
 * Use Section for distinct areas of a page with consistent
 * vertical rhythm and background colors from design tokens.
 *
 * Pages and features MUST use this instead of ad-hoc wrapper divs
 * with arbitrary background colors.
 *
 * Replaces patterns like:
 * - `className="py-12 sm:py-16 bg-gray-50"`
 * - `className="bg-slate-100 py-8"`
 * - `<div className="border-t py-12">...</div>`
 */
const Section = React.forwardRef<HTMLElement, SectionProps>(function Section(
 {
 background,
 spacing,
 border,
 fullHeight,
 className,
 children,
"aria-label": ariaLabel,
"aria-labelledby": ariaLabelledby,
 ...props
 },
 ref
): React.ReactElement {
 return (
 <section
 ref={ref}
 className={cn(
 sectionVariants({ background, spacing, border, fullHeight }),
 className
 )}
 aria-label={ariaLabel}
 aria-labelledby={ariaLabelledby}
 data-layout="section"
 data-background={background ??"transparent"}
 {...props}
 >
 {children}
 </section>
 );
});

Section.displayName ="Section";

// =============================================================================
// EXPORTS
// =============================================================================

export { Section };
